import React, { useEffect, useMemo, useState } from "react";
import { GenderAvailabilityIcons } from "@/components/GenderIcons";
import TherapistAvatars from "@/components/TherapistAvatars";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, EyeOff, Users as UsersIcon, HelpCircle, ChevronDown, LayoutGrid, List, Rows, Grid3x3, Eye } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const empty = {
  name: "", description: "", price: 0, duration_min: 60, image_path: "",
  min_notice_min: "", active: true,
  category_id: "", couples_allowed: false, price_hidden: false,
  crew_ids: [], durations: [], questions: [], questions_female: [],
  available_for_male: true, available_for_female: true,
  adults_only: false,
};

function newId() { return (crypto?.randomUUID?.() || `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`); }

export default function TreatmentsPanel() {
  const { admin } = useAuth();
  // Supervisors and crew see the same UI but with all edit / delete /
  // "New treatment" affordances hidden. Tapping a tile still opens the
  // detail dialog, but the form's <fieldset disabled> makes every input
  // read-only and the Save button is suppressed — a proper "view info"
  // surface without duplicating a second dialog.
  const canEdit = ["developer", "admin", "manager"].includes(admin?.role);
  const [items, setItems] = useState([]);
  const [crew, setCrew] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  // Fold/unfold state per category — same UX as guest menu, starts folded.
  const [openCats, setOpenCats] = useState({});
  // Density preference — persisted per device so returning crew keep their
  // favourite layout across sessions. Defaults to the dense 'tiles' view so
  // more treatments fit on a single screen without scrolling.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("admin_treatments_view_v1") || "tiles"; } catch { return "tiles"; }
  });
  useEffect(() => { try { localStorage.setItem("admin_treatments_view_v1", viewMode); } catch { /* ignore */ } }, [viewMode]);

  const load = async () => {
    const [t, c, cat] = await Promise.all([
      api.get("/admin/treatments"),
      api.get("/admin/crew"),
      api.get("/admin/treatment-categories"),
    ]);
    setItems(t.data || []);
    setCrew(c.data || []);
    setCategories(cat.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      ...empty, ...t,
      min_notice_min: t.min_notice_min ?? (t.min_notice_hours != null ? Number(t.min_notice_hours) * 60 : ""),
      category_id: t.category_id || "",
      crew_ids: t.crew_ids || [],
      durations: t.durations || [],
      questions: t.questions || [],
      questions_female: t.questions_female || [],
      // Default both to true if the treatment was created before this feature —
      // preserves the historic "unisex" behaviour on first edit.
      available_for_male: t.available_for_male !== false,
      available_for_female: t.available_for_female !== false,
      adults_only: !!t.adults_only,
    });
    setOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        duration_min: Number(form.duration_min) || 60,
        min_notice_min: form.min_notice_min === "" || form.min_notice_min === null ? null : Number(form.min_notice_min),
        image_path: form.image_path || null,
        category_id: form.category_id || null,
        crew_ids: form.crew_ids || [],
        available_for_male: !!form.available_for_male,
        available_for_female: !!form.available_for_female,
        adults_only: !!form.adults_only,
        durations: (form.durations || []).filter((d) => Number(d.minutes) > 0).map((d) => ({
          minutes: Number(d.minutes), price: Number(d.price) || 0, label: d.label || "",
        })),
        questions: (form.questions || []).filter((q) => (q.text || "").trim()).map((q) => ({
          id: q.id || newId(), text: q.text.trim(),
          type: q.type === "choice" ? "choice" : "text",
          options: q.type === "choice" ? (q.options || []).filter((o) => o.trim()) : [],
        })),
        questions_female: (form.questions_female || []).filter((q) => (q.text || "").trim()).map((q) => ({
          id: q.id || newId(), text: q.text.trim(),
          type: q.type === "choice" ? "choice" : "text",
          options: q.type === "choice" ? (q.options || []).filter((o) => o.trim()) : [],
        })),
      };
      if (form.id) await api.put(`/admin/treatments/${form.id}`, payload);
      else await api.post("/admin/treatments", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.delete(`/admin/treatments/${t.id}`);
    load();
  };

  const toggleCrew = (cid) => {
    setForm((f) => {
      const set = new Set(f.crew_ids || []);
      if (set.has(cid)) set.delete(cid); else set.add(cid);
      return { ...f, crew_ids: Array.from(set) };
    });
  };

  const addDuration = () => setForm((f) => ({ ...f, durations: [...(f.durations || []), { minutes: 60, price: 0, label: "" }] }));
  const setDuration = (i, patch) => setForm((f) => ({ ...f, durations: f.durations.map((d, idx) => idx === i ? { ...d, ...patch } : d) }));
  const removeDuration = (i) => setForm((f) => ({ ...f, durations: f.durations.filter((_, idx) => idx !== i) }));

  const addQuestion = () => setForm((f) => ({ ...f, questions: [...(f.questions || []), { id: newId(), text: "", type: "text", options: [] }] }));
  const setQuestion = (i, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));
  const removeQuestion = (i) => setForm((f) => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));

  // Group treatments by category so crew see the same foldable menu structure
  // as guests. Uncategorised items land in a synthetic "Other" bucket.
  const grouped = useMemo(() => {
    const byCat = new Map();
    for (const c of categories) byCat.set(c.id, { category: c, items: [] });
    const uncategorised = [];
    for (const t of items) {
      if (t.category_id && byCat.has(t.category_id)) byCat.get(t.category_id).items.push(t);
      else uncategorised.push(t);
    }
    const out = Array.from(byCat.values()).filter((g) => g.items.length > 0);
    if (uncategorised.length) out.push({ category: { id: "_none", name: "Other" }, items: uncategorised });
    return out;
  }, [items, categories]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Treatments</h2>
          <p className="text-sm text-muted-foreground mt-1">Menu, crew assignments, durations & optional guest questions.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex border border-border bg-card/50" role="group" aria-label="Layout density" data-testid="admin-treatments-view-toggle">
            {[
              { id: "tiles", icon: Grid3x3, label: "Tiles" },
              { id: "grid", icon: LayoutGrid, label: "Grid" },
              { id: "list", icon: List, label: "List" },
              { id: "compact", icon: Rows, label: "Compact" },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setViewMode(id)}
                aria-pressed={viewMode === id}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest transition-colors ${viewMode === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`admin-treatments-view-${id}`}
                title={`${label} view`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>
          {canEdit && (
            <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-treatment-btn">
              <Plus className="h-4 w-4 mr-2" /> New treatment
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-10" data-testid="treatments-list">
        {grouped.length === 0 && <div className="text-muted-foreground text-sm">No treatments yet.</div>}
        {grouped.map((group) => {
          const isOpen = !!openCats[group.category.id];
          return (
          <div key={group.category.id} data-testid={`admin-category-group-${group.category.id}`}>
            <button
              type="button"
              onClick={() => setOpenCats((s) => ({ ...s, [group.category.id]: !isOpen }))}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-3 mb-4 border-b border-border pb-3 text-left hover:bg-muted/30 transition-colors"
              data-testid={`admin-category-toggle-${group.category.id}`}
            >
              <ChevronDown className={`h-4 w-4 text-primary/60 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} strokeWidth={2} />
              <h3 className="font-display text-2xl tracking-tight">{group.category.name}</h3>
              <span className="overline text-primary/40 text-[10px]">{group.items.length} {group.items.length === 1 ? "treatment" : "treatments"}</span>
              <span className="ml-auto overline text-primary/40 text-[10px]">{isOpen ? "Hide" : "Show"}</span>
            </button>
            {isOpen && viewMode === "tiles" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid={`admin-tiles-${group.category.id}`}>
              {group.items.map((t) => (
                <div
                  key={t.id}
                  className="group relative bg-card border border-border overflow-hidden hover:border-accent transition-all flex flex-col"
                  data-testid={`admin-treatment-${t.id}`}
                >
                  {/* Whole tile is clickable to open the editor — the two corner icon
                      buttons stopPropagation so quick-delete stays reachable without
                      exposing a full edit surface first. */}
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="text-left flex flex-col flex-1"
                    aria-label={`Edit ${t.name}`}
                    data-testid={`edit-treatment-${t.id}`}
                  >
                    <div className="relative aspect-square bg-muted overflow-hidden">
                      {t.image_path ? (
                        <img src={fileUrl(t.image_path)} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-primary/25 text-[10px] uppercase tracking-widest">No photo</div>
                      )}
                      <span className="absolute top-2 right-2 bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] font-mono tracking-wider">
                        {t.duration_min}′
                      </span>
                      {!t.active && (
                        <span className="absolute top-2 left-2 bg-destructive/85 text-destructive-foreground px-1.5 py-0.5 text-[10px] uppercase tracking-wider">
                          Hidden
                        </span>
                      )}
                    </div>
                    <div className="p-3 flex-1 flex flex-col gap-2">
                      <h3 className="font-display text-sm sm:text-base leading-tight tracking-tight line-clamp-2">{t.name}</h3>
                      <div className="mt-auto flex items-center justify-between gap-2 pt-1 min-h-[24px]">
                        {crew.length > 0 && (t.crew_ids || []).length > 0 ? (
                          <TherapistAvatars
                            crewIds={t.crew_ids || []}
                            allCrew={crew}
                            size={22}
                            max={3}
                            testIdPrefix={`admin-treatment-${t.id}-`}
                          />
                        ) : <span />}
                        {!t.price_hidden && <span className="text-accent font-display text-sm">€{t.price}</span>}
                      </div>
                    </div>
                  </button>
                  {/* Quick-delete overlay — appears on hover, always visible on touch. */}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); remove(t); }}
                      className="absolute bottom-2 right-2 h-7 w-7 rounded-full bg-background/90 border border-border flex items-center justify-center text-destructive opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      aria-label={`Delete ${t.name}`}
                      data-testid={`delete-treatment-${t.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            )}
            {isOpen && viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.items.map((t) => (
                <div key={t.id} className="bg-card border border-border overflow-hidden hover-lift" data-testid={`admin-treatment-${t.id}`}>
                  {t.image_path && <div className="aspect-[4/3] bg-muted overflow-hidden"><img src={fileUrl(t.image_path)} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-display text-xl">{t.name}</h3>
                      <span className={`text-accent font-display ${t.price_hidden ? "opacity-50 line-through" : ""}`}>€{t.price}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {t.couples_allowed && <Badge className="text-[10px] bg-accent/20 text-accent border-0 hover:bg-accent/20">Dual</Badge>}
                      {t.adults_only && <Badge className="text-[10px] bg-primary/15 text-primary border-0 hover:bg-primary/15" data-testid={`admin-adults-only-${t.id}`}>18+</Badge>}
                      {t.price_hidden && <Badge variant="outline" className="text-[10px]"><EyeOff className="h-2.5 w-2.5 mr-1" />Price hidden</Badge>}
                      {(t.durations || []).length > 0 && <Badge variant="outline" className="text-[10px]">{t.durations.length} durations</Badge>}
                      {((t.questions || []).length + (t.questions_female || []).length) > 0 && <Badge variant="outline" className="text-[10px]"><HelpCircle className="h-2.5 w-2.5 mr-1" />{(t.questions || []).length + (t.questions_female || []).length} Q</Badge>}
                      <span className="ml-auto" data-testid={`admin-gender-${t.id}`}>
                        <GenderAvailabilityIcons
                          male={t.available_for_male !== false}
                          female={t.available_for_female !== false}
                          size={13}
                        />
                      </span>
                    </div>
                    {/* Therapist avatars — same overlapping face row the guests see, so
                        crew know at a glance who's meant to perform each treatment. */}
                    {crew.length > 0 && (
                      <div className="h-8 mb-3 flex items-center">
                        <TherapistAvatars
                          crewIds={t.crew_ids || []}
                          allCrew={crew}
                          size={26}
                          max={5}
                          testIdPrefix={`admin-treatment-${t.id}-`}
                        />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mb-3">{t.duration_min} min · {t.active ? "Active" : "Hidden"}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-treatment-${t.id}`}>
                        {canEdit ? <><Pencil className="h-3 w-3 mr-1" />Edit</> : <><Eye className="h-3 w-3 mr-1" />View</>}
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => remove(t)} className="text-destructive border-destructive/40" data-testid={`delete-treatment-${t.id}`}><Trash2 className="h-3 w-3" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            {isOpen && viewMode === "list" && (
            <div className="flex flex-col gap-3" data-testid={`admin-list-${group.category.id}`}>
              {group.items.map((t) => (
                <div key={t.id} className="bg-card border border-border overflow-hidden hover-lift flex flex-col sm:flex-row" data-testid={`admin-treatment-${t.id}`}>
                  {t.image_path ? (
                    <div className="sm:w-44 md:w-52 sm:shrink-0 aspect-[4/3] sm:aspect-square bg-muted overflow-hidden">
                      <img src={fileUrl(t.image_path)} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="sm:w-44 md:w-52 sm:shrink-0 aspect-[4/3] sm:aspect-square bg-muted/50 flex items-center justify-center text-primary/30 text-xs">
                      No photo
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-3 mb-1">
                      <h3 className="font-display text-xl truncate">{t.name}</h3>
                      <span className={`text-accent font-display shrink-0 ${t.price_hidden ? "opacity-50 line-through" : ""}`}>€{t.price}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{t.duration_min} min · {t.active ? "Active" : "Hidden"}</p>
                    {t.description && <p className="text-sm text-foreground/70 leading-relaxed line-clamp-2 mb-3">{t.description}</p>}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {t.couples_allowed && <Badge className="text-[10px] bg-accent/20 text-accent border-0 hover:bg-accent/20">Dual</Badge>}
                      {t.adults_only && <Badge className="text-[10px] bg-primary/15 text-primary border-0 hover:bg-primary/15" data-testid={`admin-adults-only-${t.id}`}>18+</Badge>}
                      {t.price_hidden && <Badge variant="outline" className="text-[10px]"><EyeOff className="h-2.5 w-2.5 mr-1" />Price hidden</Badge>}
                      {(t.durations || []).length > 0 && <Badge variant="outline" className="text-[10px]">{t.durations.length} durations</Badge>}
                      {((t.questions || []).length + (t.questions_female || []).length) > 0 && <Badge variant="outline" className="text-[10px]"><HelpCircle className="h-2.5 w-2.5 mr-1" />{(t.questions || []).length + (t.questions_female || []).length} Q</Badge>}
                      <span className="ml-auto" data-testid={`admin-gender-${t.id}`}>
                        <GenderAvailabilityIcons male={t.available_for_male !== false} female={t.available_for_female !== false} size={13} />
                      </span>
                    </div>
                    <div className="mt-auto flex items-center justify-between gap-3">
                      {crew.length > 0 ? (
                        <TherapistAvatars crewIds={t.crew_ids || []} allCrew={crew} size={24} max={4} testIdPrefix={`admin-treatment-${t.id}-`} />
                      ) : <span />}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-treatment-${t.id}`}>
                          {canEdit ? <><Pencil className="h-3 w-3 mr-1" />Edit</> : <><Eye className="h-3 w-3 mr-1" />View</>}
                        </Button>
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => remove(t)} className="text-destructive border-destructive/40" data-testid={`delete-treatment-${t.id}`}><Trash2 className="h-3 w-3" /></Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            {isOpen && viewMode === "compact" && (
            <div className="border border-border divide-y divide-border bg-card" data-testid={`admin-compact-${group.category.id}`}>
              {group.items.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors" data-testid={`admin-treatment-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-display text-base truncate">{t.name}</span>
                      {!t.active && <Badge variant="outline" className="text-[9px]">Hidden</Badge>}
                      {t.couples_allowed && <Badge className="text-[9px] bg-accent/20 text-accent border-0 hover:bg-accent/20">Dual</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-3 mt-0.5">
                      <span>{t.duration_min} min</span>
                      <GenderAvailabilityIcons male={t.available_for_male !== false} female={t.available_for_female !== false} size={11} />
                      {(t.durations || []).length > 0 && <span className="opacity-70">{t.durations.length} durations</span>}
                    </div>
                  </div>
                  <span className={`font-display text-base text-accent shrink-0 ${t.price_hidden ? "opacity-50 line-through" : ""}`}>€{t.price}</span>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-treatment-${t.id}`}>
                      {canEdit ? <Pencil className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => remove(t)} className="text-destructive border-destructive/40" data-testid={`delete-treatment-${t.id}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="treatment-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{canEdit ? (form.id ? "Edit" : "New") : "Treatment"} {canEdit ? "treatment" : "info"}</DialogTitle></DialogHeader>
          {!canEdit && (
            <div className="border border-accent/40 bg-accent/5 px-3 py-2 text-xs text-accent flex items-center gap-2" data-testid="treatment-readonly-banner">
              <Eye className="h-3 w-3" /> Read-only view · your role can browse but not edit the menu.
            </div>
          )}
          <fieldset disabled={!canEdit} className={!canEdit ? "opacity-90 space-y-6" : "space-y-6 contents"}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div><Label className="overline text-primary/60">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tf-name" className="mt-2" /></div>
              <div><Label className="overline text-primary/60">Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tf-desc" className="mt-2" /></div>
              <div>
                <Label className="overline text-primary/60">Category</Label>
                <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="mt-2 w-full h-10 border border-border bg-background px-3 text-sm" data-testid="tf-category">
                  <option value="">— none —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Manage categories in the Categories tab.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline text-primary/60">Default duration (min)</Label>
                  <Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="tf-duration" className="mt-2" /></div>
                <div><Label className="overline text-primary/60">Default price (€)</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="tf-price" className="mt-2" /></div>
              </div>
              <div><Label className="overline text-primary/60">Min notice override (minutes) — empty = global</Label>
                <Input type="number" value={form.min_notice_min} onChange={(e) => setForm({ ...form, min_notice_min: e.target.value })} data-testid="tf-notice" className="mt-2" /></div>
            </div>

            {/* Alt durations */}
            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="overline text-primary/60">Alternative durations & prices</Label>
                <Button size="sm" variant="outline" onClick={addDuration} data-testid="tf-add-duration"><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Guests can choose from the default plus any listed here.</p>
              {(form.durations || []).length === 0 && <p className="text-xs text-muted-foreground italic">No alternatives — only default duration will be offered.</p>}
              <div className="space-y-2">
                {(form.durations || []).map((d, i) => (
                  // Stable per-row key — falls back to a synthetic once so React
                  // never recycles the row on reorder / delete.
                  <div key={d._k || (d._k = `dur-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)} className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-center" data-testid={`tf-duration-row-${i}`}>
                    <Input type="number" value={d.minutes} onChange={(e) => setDuration(i, { minutes: e.target.value })} placeholder="min" />
                    <Input type="number" value={d.price} onChange={(e) => setDuration(i, { price: e.target.value })} placeholder="€" />
                    <Input value={d.label || ""} onChange={(e) => setDuration(i, { label: e.target.value })} placeholder="Label (optional)" />
                    <Button size="sm" variant="outline" onClick={() => removeDuration(i)} className="text-destructive border-destructive/40"><X className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Crew */}
            <div className="border-t border-border pt-4">
              <Label className="overline text-primary/60 flex items-center gap-2 mb-2"><UsersIcon className="h-3 w-3" /> Providing crew</Label>
              <p className="text-xs text-muted-foreground mb-3">Uncheck all to allow any crew member.</p>
              <div className="flex flex-wrap gap-2" data-testid="tf-crew-list">
                {crew.length === 0 && <p className="text-xs text-muted-foreground italic">Add crew members first.</p>}
                {crew.map((c) => {
                  const checked = (form.crew_ids || []).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCrew(c.id)}
                      className={`px-4 py-2 border text-sm rounded-full transition-colors ${checked ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                      data-testid={`tf-crew-${c.id}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Questions */}
            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="overline text-primary/60 flex items-center gap-2"><HelpCircle className="h-3 w-3" /> Questions (all guests)</Label>
                <Button size="sm" variant="outline" onClick={addQuestion} data-testid="tf-add-question"><Plus className="h-3 w-3 mr-1" />Add question</Button>
              </div>
              <div className="space-y-4">
                {(form.questions || []).map((q, i) => (
                  <div key={q.id || i} className="border border-border p-4 space-y-2" data-testid={`tf-question-${i}`}>
                    <div className="flex justify-between gap-2">
                      <Input value={q.text} onChange={(e) => setQuestion(i, { text: e.target.value })} placeholder="Question text" className="flex-1" />
                      <select value={q.type} onChange={(e) => setQuestion(i, { type: e.target.value })} className="border border-border bg-background px-3 h-10 text-sm">
                        <option value="text">Free text</option>
                        <option value="choice">Multiple choice</option>
                      </select>
                      <Button size="sm" variant="outline" onClick={() => removeQuestion(i)} className="text-destructive border-destructive/40"><X className="h-3 w-3" /></Button>
                    </div>
                    {q.type === "choice" && (
                      <div>
                        <Label className="overline text-primary/50 text-[10px]">Options (comma separated)</Label>
                        <Input value={(q.options || []).join(", ")} onChange={(e) => setQuestion(i, { options: e.target.value.split(",").map((s) => s.trim()) })} placeholder="Yes, No, Not sure" className="mt-1" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Female-only Questions */}
            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="overline text-primary/60 flex items-center gap-2"><HelpCircle className="h-3 w-3 text-accent" /> Questions (female guests only)</Label>
                <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, questions_female: [...(f.questions_female || []), { id: newId(), text: "", type: "text", options: [] }] }))} data-testid="tf-add-question-female">
                  <Plus className="h-3 w-3 mr-1" />Add female-only
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Shown only when the guest is marked female.</p>
              <div className="space-y-4">
                {(form.questions_female || []).map((q, i) => (
                  <div key={q.id || i} className="border border-accent/40 bg-accent/5 p-4 space-y-2" data-testid={`tf-question-female-${i}`}>
                    <div className="flex justify-between gap-2">
                      <Input value={q.text} onChange={(e) => setForm((f) => ({ ...f, questions_female: f.questions_female.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x) }))} placeholder="Question text" className="flex-1" />
                      <select value={q.type} onChange={(e) => setForm((f) => ({ ...f, questions_female: f.questions_female.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x) }))} className="border border-border bg-background px-3 h-10 text-sm">
                        <option value="text">Free text</option>
                        <option value="choice">Multiple choice</option>
                      </select>
                      <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, questions_female: f.questions_female.filter((_, idx) => idx !== i) }))} className="text-destructive border-destructive/40"><X className="h-3 w-3" /></Button>
                    </div>
                    {q.type === "choice" && (
                      <div>
                        <Label className="overline text-primary/50 text-[10px]">Options (comma separated)</Label>
                        <Input value={(q.options || []).join(", ")} onChange={(e) => setForm((f) => ({ ...f, questions_female: f.questions_female.map((x, idx) => idx === i ? { ...x, options: e.target.value.split(",").map((s) => s.trim()) } : x) }))} placeholder="Yes, No, Not sure" className="mt-1" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="border-t border-border pt-4">
              <Label className="overline text-primary/60">Template photo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.image_path && <img src={fileUrl(form.image_path)} alt="" className="w-20 h-20 object-cover border border-border" />}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload / change"}
                  <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="tf-image" />
                </label>
                {form.image_path && (
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, image_path: "" })} className="text-destructive border-destructive/40">Remove</Button>
                )}
              </div>
            </div>

            {/* Toggles */}
            <div className="border-t border-border pt-4 grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="tf-active" />
                <Label>Visible on guest menu</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.price_hidden} onCheckedChange={(v) => setForm({ ...form, price_hidden: v })} data-testid="tf-price-hidden" />
                <Label>Hide price from guests (crew only sees it)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.couples_allowed} onCheckedChange={(v) => setForm({ ...form, couples_allowed: v })} data-testid="tf-couples" />
                <Label>Available as dual treatment · same cabin or invited friend</Label>
              </div>
              <div className="border border-border/60 bg-muted/30 p-3 space-y-2">
                <Label className="overline text-primary/60 text-[10px]">Gender availability</Label>
                <label className="flex items-center gap-3 cursor-pointer" data-testid="tf-available-male-row">
                  <input
                    type="checkbox"
                    checked={form.available_for_male !== false}
                    onChange={(e) => setForm({ ...form, available_for_male: e.target.checked })}
                    className="h-4 w-4 accent-primary"
                    data-testid="tf-available-male"
                  />
                  <span className="text-sm">Available for male guests</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer" data-testid="tf-available-female-row">
                  <input
                    type="checkbox"
                    checked={form.available_for_female !== false}
                    onChange={(e) => setForm({ ...form, available_for_female: e.target.checked })}
                    className="h-4 w-4 accent-primary"
                    data-testid="tf-available-female"
                  />
                  <span className="text-sm">Available for female guests</span>
                </label>
                <p className="text-[11px] text-muted-foreground">Both selected = unisex (historic default). Un-checking one hides the treatment from guests of that gender.</p>
              </div>

              <label className="flex items-start gap-3 border border-border bg-muted/30 p-3 cursor-pointer" data-testid="tf-adults-only-row">
                <input
                  type="checkbox"
                  checked={!!form.adults_only}
                  onChange={(e) => setForm({ ...form, adults_only: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-primary"
                  data-testid="tf-adults-only"
                />
                <span className="text-sm">
                  <span className="font-medium">Adults only (18+)</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5">
                    Under-18 guests still see the treatment on the menu but the Reserve button is disabled with a polite note. Set at the category level to cover a whole group at once.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setOpen(false)}>{canEdit ? "Cancel" : "Close"}</Button>
              {canEdit && (
                <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="tf-save">Save</Button>
              )}
            </div>
          </div>
          </fieldset>
        </DialogContent>
      </Dialog>
    </div>
  );
}
